import { useState } from 'react';
import { useDebouncedValue } from '@gonfalon/async/useDebouncedValue';
import { msg } from '@gonfalon/messages/msg';
import { listDatasetsQuery } from '@gonfalon/rest-api/queries/datasets';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import type { DatasetOption } from './types';

import styles from './DatasetPicker.module.css';

export interface DatasetPickerProps {
  /** Project key used to list the project's datasets. */
  projectKey: string;
  onSelect: (dataset: DatasetOption) => void;
  isDisabled?: boolean;
  /** Distinct from `isDisabled` so the "Adding…" label only shows while a promotion is actually running. */
  isPromoting?: boolean;
}

/**
 * A "+ Add to dataset" trigger that opens a searchable list of the project's datasets. Selecting
 * one calls `onSelect`. Built entirely from LaunchPad components; the dataset collection comes from
 * the datasets API, which filters server-side via its `filter` query param (so we don't pass a
 * client-side `filter` to the Autocomplete).
 */
export const DatasetPicker = ({
  projectKey,
  onSelect,
  isDisabled = false,
  isPromoting = false,
}: DatasetPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());

  // Only hit the API once the picker is opened; the server filters by the debounced search term.
  const { data, isFetching } = useQuery({
    ...listDatasetsQuery({ projKey: projectKey, query: { filter: debouncedSearch || undefined } }),
    enabled: isOpen,
  });

  const options: DatasetOption[] = (data?.datasets ?? []).map((dataset) => ({
    id: dataset.id,
    name: dataset.name ?? dataset.filename,
  }));

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        aria-label={msg('observability.datasetPicker.addToDatasetLabel')}
        variant="default"
        isDisabled={isDisabled}
      >
        <Icon name="add" size="small" slot="start-icon" />
        {isPromoting ? 'Adding…' : 'Add to dataset'}
      </Button>
      <Popover placement="bottom start" className={styles.popover}>
        <Dialog aria-label={msg('observability.datasetPicker.addToDatasetLabel')}>
          <Autocomplete inputValue={search} onInputChange={setSearch}>
            <SearchField aria-label={msg('observability.datasetPicker.searchLabel')} autoFocus>
              <Group>
                <Icon name="search" size="small" />
                <Input placeholder={msg('observability.datasetPicker.searchLabel')} />
              </Group>
            </SearchField>
            <ListBox
              aria-label={msg('observability.datasetPicker.listLabel')}
              items={options}
              renderEmptyState={() => (
                <div className={styles.emptyState}>{isFetching ? 'Loading datasets…' : 'No datasets found'}</div>
              )}
              onAction={(key) => {
                const dataset = options.find((option) => option.id === String(key));
                if (dataset) {
                  onSelect(dataset);
                  setIsOpen(false);
                }
              }}
            >
              {(dataset) => (
                <ListBoxItem id={dataset.id} textValue={dataset.name}>
                  {dataset.name}
                </ListBoxItem>
              )}
            </ListBox>
          </Autocomplete>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
